<template>
  <aside>
    <div class="filter-controls">
      <filters-add
        :filters="filters"
        :selected-filters="selectedFilters"
        @selectFilters="setSelectedFilters"
      />
      <filters-clear @clear="clearAndCancelPending" />
      <div class="spacer" />
      <filters-sort
        :sort="sort"
        :direction="direction"
        @setSort="setSort"
      />
      <view-mode
        v-if="!isWidget"
        class="is-hidden-mobile"
        :view-mode-key="viewModeKey"
        @setMode="setMode"
      />
    </div>
    <div class="filter-items">
      <filter-item
        v-for="filter in selectedFilters"
        :key="filter.field"
        :filter="filter"
        :selected-options="selectedOptions"
        @remove="removeSelectedFilterAndCancelPending"
        @updateOption="updateSelectedFilterOptions"
      />
    </div>
  </aside>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import debounce from 'lodash.debounce'
import FiltersAdd from '@/components/common/filters/FiltersAdd'
import FilterItem from '@/components/common/filters/FilterItem'
import FiltersClear from '@/components/common/filters/FiltersClear'
import FiltersSort from '@/components/common/filters/FiltersSort'
import ViewMode from '@/components/common/filters/ViewMode'

export default {
  name: 'SearchFilterBar',
  components: {
    FiltersSort,
    FiltersClear,
    FilterItem,
    FiltersAdd,
    ViewMode
  },
  data () {
    return {
      // Keep separate debounce timers per field so removing one filter
      // does not cancel pending edits for unrelated filters.
      pendingFilterUpdates: {}
    }
  },
  props: {
    exactMatch: {
      type: Boolean
    }
  },
  computed: {
    ...mapState({
      filters: ({ search }) => search.filters.items,
      selectedOptions: ({ search }) => search.filters.selected,
      sort: ({ search }) => search.sort,
      direction: ({ search }) => search.direction,
      viewModeKey: ({ search }) => search.viewModeKey
    }),
    ...mapGetters({
      selectedFilters: 'search/selectedFilters',
      isWidget: 'widgets/isWidget'
    })
  },
  methods: {
    ...mapActions({
      setSelectedFilters: 'search/setSelectedFilters',
      removeSelectedFilter: 'search/removeSelectedFilter',
      dispatchUpdateSelectedFilterOptions: 'search/updateSelectedFilterOptions',
      clear: 'search/clearSelectedFilters',
      setSort: 'search/setSort',
      resetOffset: 'search/resetOffset',
      search: 'search/search'
    }),
    getFilterDebounceKey (filter) {
      return filter?.field || null
    },
    getDebouncedUpdater (field) {
      if (!this.pendingFilterUpdates[field]) {
        this.pendingFilterUpdates[field] = debounce((payload) => {
          this.dispatchUpdateSelectedFilterOptions(payload)
        }, 500)
      }
      return this.pendingFilterUpdates[field]
    },
    updateSelectedFilterOptions (payload) {
      const key = this.getFilterDebounceKey(payload?.filter)
      if (!key) {
        this.dispatchUpdateSelectedFilterOptions(payload)
        return
      }
      this.getDebouncedUpdater(key)(payload)
    },
    cancelPendingFilterUpdate (filter) {
      const key = this.getFilterDebounceKey(filter)
      if (!key) return
      if (this.pendingFilterUpdates[key]) {
        this.pendingFilterUpdates[key].cancel()
        delete this.pendingFilterUpdates[key]
      }
    },
    cancelAllPendingFilterUpdates () {
      Object.keys(this.pendingFilterUpdates).forEach((key) => {
        this.pendingFilterUpdates[key].cancel()
        delete this.pendingFilterUpdates[key]
      })
    },
    async setMode(key) {
      this.$store.dispatch('search/setMode', key)
      if (this.isWidget) {
        this.resetOffset()
        const { q = '' } = this.$route.query
        await this.search({ q, exactMatch: this.exactMatch })
      }
    },
    removeSelectedFilterAndCancelPending (filter) {
      this.cancelPendingFilterUpdate(filter)
      this.removeSelectedFilter(filter)
    },
    clearAndCancelPending () {
      this.cancelAllPendingFilterUpdates()
      this.clear()
    }
  },
  beforeUnmount () {
    this.cancelAllPendingFilterUpdates()
  }
}
</script>

<style scoped>
.filter-controls {
  display: flex;
  flex-direction: row;
  padding: .15rem .35rem;
  margin: 0 -0.15rem;
}
.spacer {
  flex: 1 1 0;
}
.filter-controls > * {
  padding: 0 0.15rem;
}
.filter-items {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 0 .25rem;
}
.filter-items > div {
  margin: .15rem .25rem .25rem .15rem !important;
}
</style>
